/**
 * =============================================================================
 * RFI CREATE FORM COMPONENT
 * =============================================================================
 *
 * Minimal, collapsible "open an RFI" form (Decision D-1): a message textarea +
 * an add/remove item-row list (up to MAX_RFI_ITEMS). Each row = an itemType
 * select, a label, a required checkbox, a `unit` input (measurement only), and
 * an options editor (selection only). No templates, rich text, icons, or
 * drag-reorder.
 *
 * The form does UX normalization (D-1): trims message/labels/unit/options,
 * drops empty/whitespace draft options, disallows duplicate options, clears
 * `unit` when the type leaves `measurement` / `options` when it leaves
 * `selection`, and blocks submit when a `selection` item has zero options.
 * Client-side validation is UX-only; the worker 409 is the authoritative gate.
 *
 * Status gating is INITIAL UX only (D-1): the (stale) `questionStatus` prop
 * disables the form when the question is clearly outside RFI_ELIGIBLE_STATUSES,
 * but the backend remains authoritative.
 *
 * =============================================================================
 */

import React, { useCallback, useRef, useState } from 'react';
import { Box, Button, Checkbox, Flex, FlexItem, Input, Text, Textarea } from '@bigcommerce/big-design';
import { CONTENT_VALIDATION, RFI_ITEM_TYPES } from '@find-my-part/types';
import type { CreateRfiItemInput, CreateRfiRequest, RfiItemType } from '@find-my-part/types';
import { RFI_ITEM_TYPE_LABELS } from './rfiLabels';
import { tokens } from '../../../tokens';

/**
 * Question statuses on which an RFI may be opened (`RFI_ELIGIBLE_STATUSES`,
 * worker `RfiService`). UI-only initial gate; kept as a runtime list because
 * `AdminQuestionListItem.status` is typed `string`.
 */
const RFI_ELIGIBLE_STATUSES: readonly string[] = ['OPEN', 'IN_PROGRESS', 'NEEDS_MORE_INFO'];

/** A local draft item (the `key` is a render key only — never sent). */
interface DraftItem {
  key: number;
  itemType: RfiItemType;
  label: string;
  required: boolean;
  unit: string;
  options: string[];
  optionDraft: string;
}

/**
 * Props for RfiCreateForm.
 */
export interface RfiCreateFormProps {
  /** Current question status — gates the form (UX-only; backend authoritative). */
  questionStatus: string;
  /** Open the RFI. Resolves true on success (form resets + collapses). */
  onCreate: (input: CreateRfiRequest) => Promise<boolean>;
  /** Optional test ID prefix. */
  testId?: string;
}

function makeEmptyItem(key: number): DraftItem {
  return {
    key,
    itemType: 'text',
    label: '',
    required: true,
    unit: '',
    options: [],
    optionDraft: '',
  };
}

/** Normalize + dedupe an item's options (trim, drop empties, no duplicates). */
function normalizeOptions(options: string[]): string[] {
  const result: string[] = [];
  for (const raw of options) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    if (result.includes(trimmed)) continue;
    result.push(trimmed);
  }
  return result;
}

/** Whether a single draft item passes UX validation. */
function isItemValid(item: DraftItem): boolean {
  const label = item.label.trim();
  if (
    label.length < CONTENT_VALIDATION.MIN_RFI_ITEM_LABEL_LENGTH ||
    label.length > CONTENT_VALIDATION.MAX_RFI_ITEM_LABEL_LENGTH
  ) {
    return false;
  }
  if (item.itemType === 'measurement' && item.unit.trim().length > CONTENT_VALIDATION.MAX_RFI_ITEM_UNIT_LENGTH) {
    return false;
  }
  if (item.itemType === 'selection') {
    const options = normalizeOptions(item.options);
    if (
      options.length < 1 ||
      options.length > CONTENT_VALIDATION.MAX_RFI_ITEM_OPTIONS ||
      options.some((option) => option.length > CONTENT_VALIDATION.MAX_RFI_ITEM_OPTION_LENGTH)
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Collapsible minimal RFI create form.
 */
export function RfiCreateForm({
  questionStatus,
  onCreate,
  testId,
}: RfiCreateFormProps): React.ReactElement {
  const keyCounterRef = useRef(1);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [items, setItems] = useState<DraftItem[]>(() => [makeEmptyItem(0)]);

  const isEligible = RFI_ELIGIBLE_STATUSES.includes(questionStatus);

  const updateItem = useCallback((key: number, patch: Partial<DraftItem>) => {
    setItems((prev) => prev.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  }, []);

  const handleTypeChange = useCallback(
    (key: number, itemType: RfiItemType) => {
      setItems((prev) =>
        prev.map((item) => {
          if (item.key !== key) return item;
          return {
            ...item,
            itemType,
            // Clear stale type-specific fields so they can't ride along.
            unit: itemType === 'measurement' ? item.unit : '',
            options: itemType === 'selection' ? item.options : [],
            optionDraft: itemType === 'selection' ? item.optionDraft : '',
          };
        })
      );
    },
    []
  );

  const addItem = useCallback(() => {
    setItems((prev) => {
      if (prev.length >= CONTENT_VALIDATION.MAX_RFI_ITEMS) return prev;
      const key = keyCounterRef.current;
      keyCounterRef.current += 1;
      return [...prev, makeEmptyItem(key)];
    });
  }, []);

  const removeItem = useCallback((key: number) => {
    setItems((prev) => (prev.length <= 1 ? prev : prev.filter((item) => item.key !== key)));
  }, []);

  const addOption = useCallback((key: number) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.key !== key) return item;
        const draft = item.optionDraft.trim();
        if (!draft) return item;
        if (draft.length > CONTENT_VALIDATION.MAX_RFI_ITEM_OPTION_LENGTH) return item;
        if (item.options.length >= CONTENT_VALIDATION.MAX_RFI_ITEM_OPTIONS) return item;
        if (item.options.includes(draft)) return { ...item, optionDraft: '' };
        return { ...item, options: [...item.options, draft], optionDraft: '' };
      })
    );
  }, []);

  const removeOption = useCallback((key: number, option: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, options: item.options.filter((o) => o !== option) } : item
      )
    );
  }, []);

  const resetForm = useCallback(() => {
    keyCounterRef.current = 1;
    setMessage('');
    setItems([makeEmptyItem(0)]);
    setIsExpanded(false);
  }, []);

  const trimmedMessage = message.trim();
  const messageValid =
    trimmedMessage.length >= CONTENT_VALIDATION.MIN_RFI_MESSAGE_LENGTH &&
    trimmedMessage.length <= CONTENT_VALIDATION.MAX_RFI_MESSAGE_LENGTH;
  const itemsValid =
    items.length >= CONTENT_VALIDATION.MIN_RFI_ITEMS &&
    items.length <= CONTENT_VALIDATION.MAX_RFI_ITEMS &&
    items.every(isItemValid);
  const canSubmit = isEligible && messageValid && itemsValid && !isSubmitting;

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      // Build the typed request field-by-field (the client reconstructs again).
      const requestItems: CreateRfiItemInput[] = items.map((item) => {
        const out: CreateRfiItemInput = {
          itemType: item.itemType,
          label: item.label.trim(),
          required: item.required,
        };
        if (item.itemType === 'measurement') {
          const unit = item.unit.trim();
          if (unit) out.unit = unit;
        }
        if (item.itemType === 'selection') {
          out.options = normalizeOptions(item.options);
        }
        return out;
      });
      const succeeded = await onCreate({ message: trimmedMessage, items: requestItems });
      if (succeeded) {
        resetForm();
      }
    } catch {
      // The hook owns user-facing error messaging; preserve the draft.
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, items, trimmedMessage, onCreate, resetForm]);

  if (!isEligible) {
    return (
      <Box marginBottom="medium" data-testid={testId}>
        <Text
          color="secondary"
          data-testid={testId ? `${testId}-disabled-note` : undefined}
        >
          RFI can be opened only on Open / In progress / Needs more info questions.
        </Text>
      </Box>
    );
  }

  if (!isExpanded) {
    return (
      <Box marginBottom="medium" data-testid={testId}>
        <Button
          variant="secondary"
          onClick={() => setIsExpanded(true)}
          data-testid={testId ? `${testId}-open` : undefined}
        >
          New RFI request
        </Button>
      </Box>
    );
  }

  return (
    <Box
      marginBottom="medium"
      padding="small"
      style={{ border: `1px solid ${tokens.color.border.default}`, borderRadius: tokens.dimension.radius.md }}
      data-testid={testId}
    >
      <Text bold>
        New RFI request
      </Text>

      <Box marginTop="small">
        <Textarea
          label="Message to the customer"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          maxLength={CONTENT_VALIDATION.MAX_RFI_MESSAGE_LENGTH}
          placeholder="Explain what additional information is needed…"
          aria-label="RFI message"
          data-testid={testId ? `${testId}-message` : undefined}
        />
        <Text color="secondary">
          {trimmedMessage.length} / {CONTENT_VALIDATION.MAX_RFI_MESSAGE_LENGTH.toLocaleString()} (min{' '}
          {CONTENT_VALIDATION.MIN_RFI_MESSAGE_LENGTH})
        </Text>
      </Box>

      <Box marginTop="small">
        <Text bold>
          Requested items
        </Text>
        {items.map((item, index) => (
          <Box
            key={item.key}
            marginTop="xSmall"
            padding="small"
            style={{ border: `1px solid ${tokens.color.border.default}`, borderRadius: tokens.dimension.radius.md }}
            data-testid={testId ? `${testId}-item-${index}` : undefined}
          >
            <Flex flexGap="0.5rem" alignItems="flex-end" flexWrap="wrap">
              <FlexItem>
                <label style={{ display: 'block' }}>
                  <Text>Type</Text>
                  <select
                    value={item.itemType}
                    onChange={(e) => handleTypeChange(item.key, e.target.value as RfiItemType)}
                    aria-label={`Item ${index + 1} type`}
                    data-testid={testId ? `${testId}-item-${index}-type` : undefined}
                    style={{ padding: tokens.dimension.space.xsm, marginTop: tokens.dimension.space.xxs }}
                  >
                    {RFI_ITEM_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {RFI_ITEM_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </select>
                </label>
              </FlexItem>
              <FlexItem style={{ flex: 1, minWidth: '160px' }}>
                <Input
                  label="Label"
                  value={item.label}
                  onChange={(e) => updateItem(item.key, { label: e.target.value })}
                  maxLength={CONTENT_VALIDATION.MAX_RFI_ITEM_LABEL_LENGTH}
                  placeholder="What to provide (e.g. a photo of the latch)"
                  aria-label={`Item ${index + 1} label`}
                  data-testid={testId ? `${testId}-item-${index}-label` : undefined}
                />
              </FlexItem>
              <FlexItem>
                <Checkbox
                  label="Required"
                  checked={item.required}
                  onChange={() => updateItem(item.key, { required: !item.required })}
                  data-testid={testId ? `${testId}-item-${index}-required` : undefined}
                />
              </FlexItem>
              {items.length > 1 && (
                <FlexItem>
                  <Button
                    variant="subtle"
                    onClick={() => removeItem(item.key)}
                    aria-label={`Remove item ${index + 1}`}
                    data-testid={testId ? `${testId}-item-${index}-remove` : undefined}
                  >
                    Remove
                  </Button>
                </FlexItem>
              )}
            </Flex>

            {item.itemType === 'measurement' && (
              <Box marginTop="xSmall" style={{ maxWidth: '200px' }}>
                <Input
                  label="Unit (optional)"
                  value={item.unit}
                  onChange={(e) => updateItem(item.key, { unit: e.target.value })}
                  maxLength={CONTENT_VALIDATION.MAX_RFI_ITEM_UNIT_LENGTH}
                  placeholder="e.g. mm"
                  aria-label={`Item ${index + 1} unit`}
                  data-testid={testId ? `${testId}-item-${index}-unit` : undefined}
                />
              </Box>
            )}

            {item.itemType === 'selection' && (
              <Box marginTop="xSmall" data-testid={testId ? `${testId}-item-${index}-options` : undefined}>
                <Flex flexGap="0.5rem" alignItems="flex-end">
                  <FlexItem style={{ flex: 1 }}>
                    <Input
                      label="Add an option"
                      value={item.optionDraft}
                      onChange={(e) => updateItem(item.key, { optionDraft: e.target.value })}
                      maxLength={CONTENT_VALIDATION.MAX_RFI_ITEM_OPTION_LENGTH}
                      placeholder="Option label"
                      aria-label={`Item ${index + 1} option draft`}
                      data-testid={testId ? `${testId}-item-${index}-option-draft` : undefined}
                    />
                  </FlexItem>
                  <FlexItem>
                    <Button
                      variant="secondary"
                      onClick={() => addOption(item.key)}
                      disabled={item.optionDraft.trim().length === 0}
                      data-testid={testId ? `${testId}-item-${index}-option-add` : undefined}
                    >
                      Add
                    </Button>
                  </FlexItem>
                </Flex>
                <Flex flexGap="0.25rem" flexWrap="wrap" marginTop="xSmall">
                  {item.options.map((option) => (
                    <FlexItem key={option}>
                      <Box
                        padding="xSmall"
                        style={{
                          backgroundColor: tokens.color.status.info.surface,
                          borderRadius: tokens.dimension.radius.sm,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: tokens.dimension.space.xs,
                        }}
                      >
                        <Text>{option}</Text>
                        <Button
                          variant="subtle"
                          onClick={() => removeOption(item.key, option)}
                          aria-label={`Remove option ${option}`}
                          data-testid={
                            testId ? `${testId}-item-${index}-option-remove-${option}` : undefined
                          }
                        >
                          ✕
                        </Button>
                      </Box>
                    </FlexItem>
                  ))}
                </Flex>
                {item.options.length === 0 && (
                  <Text color="secondary">
                    A selection item needs at least one option.
                  </Text>
                )}
              </Box>
            )}
          </Box>
        ))}

        <Box marginTop="xSmall">
          <Button
            variant="subtle"
            onClick={addItem}
            disabled={items.length >= CONTENT_VALIDATION.MAX_RFI_ITEMS}
            data-testid={testId ? `${testId}-add-item` : undefined}
          >
            Add item
          </Button>
        </Box>
      </Box>

      <Flex justifyContent="flex-end" flexGap="0.5rem" marginTop="small">
        <FlexItem>
          <Button
            variant="subtle"
            onClick={resetForm}
            disabled={isSubmitting}
            data-testid={testId ? `${testId}-cancel` : undefined}
          >
            Cancel
          </Button>
        </FlexItem>
        <FlexItem>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            isLoading={isSubmitting}
            data-testid={testId ? `${testId}-submit` : undefined}
          >
            Open RFI
          </Button>
        </FlexItem>
      </Flex>
    </Box>
  );
}
